// ts-rest contract for the KPI surface.
//
// The KPI dashboard (`/view/kpi`) and the Ops Fees & Billing
// AUM overview are computed LIVE from the ValuationGrid (`client_holdings`) +
// the canonical client roster + Yahoo pricing — the same engine that backs the
// Desk Dashboard. The legacy six-file Tercero KPI upload + `KpiMonthSnapshot`
// rollup has been removed; what the holdings cannot express (net flows, income,
// a performance decomposition, historical backfill) is documented in
// `docs/kpi-gaps.md`.
//
// Two endpoints:
//   GET /api/kpi/firm — firm-wide current AUM/counts/YTD + per-category +
//        per-manager breakdowns + the month-over-month AUM trend.
//   GET /api/kpi/my   — the signed-in IM's own slice + firm totals for
//        "my % of firm" comparisons + the IM's own AUM trend.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

export const REPORTING_CATEGORIES = ['Advisory', 'Disc & Adv Managed', 'Execution Only'] as const;
export type ReportingCategory = (typeof REPORTING_CATEGORIES)[number];

// ---------------------------------------------------------------------------
// Aggregate shapes — only the figures the live holdings can express.
// ---------------------------------------------------------------------------

/** The metric bundle for the firm, a reporting category, or one manager. */
export const kpiAggregateSchema = z.object({
    /** Closing AUM (£) — sum of holding valuations in scope. */
    endValue: z.number(),
    totalClients: z.number().int().nonnegative(),
    totalPortfolios: z.number().int().nonnegative(),
    /** AUM-weighted YTD % across in-scope clients; null when coverage is too thin. */
    avgYtdPct: z.number().nullable(),
});
export type KpiAggregateDto = z.infer<typeof kpiAggregateSchema>;

export const kpiManagerAggregateSchema = kpiAggregateSchema.extend({
    investmentManagerUserId: z.string(),
    investmentManager: z.string(),
});
export type KpiManagerAggregateDto = z.infer<typeof kpiManagerAggregateSchema>;

const perCategorySchema = z.object({
    Advisory: kpiAggregateSchema,
    'Disc & Adv Managed': kpiAggregateSchema,
    'Execution Only': kpiAggregateSchema,
});
export type KpiPerCategoryDto = z.infer<typeof perCategorySchema>;

const perManagerSchema = z.object({
    Advisory: z.array(kpiManagerAggregateSchema),
    'Disc & Adv Managed': z.array(kpiManagerAggregateSchema),
    'Execution Only': z.array(kpiManagerAggregateSchema),
});
export type KpiPerManagerDto = z.infer<typeof perManagerSchema>;

/** One point on the month-over-month AUM trend (from retained AumSnapshots). */
export const kpiTrendPointSchema = z.object({
    month: z.string().regex(/^\d{4}-\d{2}$/),
    firmAumGbp: z.number(),
    perCategory: z.object({
        Advisory: z.number(),
        'Disc & Adv Managed': z.number(),
        'Execution Only': z.number(),
    }),
});
export type KpiTrendPointDto = z.infer<typeof kpiTrendPointSchema>;

// ---------------------------------------------------------------------------
// GET /api/kpi/firm
// ---------------------------------------------------------------------------

export const kpiFirmTotalsSchema = kpiAggregateSchema.extend({
    /** Gross AUM change vs the prior month (null until two months exist). */
    aumChangeGbp: z.number().nullable(),
});
export type KpiFirmTotalsDto = z.infer<typeof kpiFirmTotalsSchema>;

/**
 * AUM (Discretionary) vs AUA (Advisory + Execution-Only) split (Josh-confirmed,
 * Jun 2026), computed live from the RAW `Client.serviceType` — not derivable
 * from `perCategory`, which lumps Discretionary into the managed bucket.
 * `endValue` (total AUM) above still includes every serviceType; these two split
 * out the Discretionary vs Advisory+XO line, and need not sum to `endValue`
 * (a null / unrecognised serviceType is in neither).
 */
export const kpiAumAuaSchema = z.object({
    /** Discretionary clients' closing value (£). */
    aumGbp: z.number(),
    /** Advisory + Execution-Only clients' closing value (£). */
    auaGbp: z.number(),
});
export type KpiAumAuaDto = z.infer<typeof kpiAumAuaSchema>;

export const kpiFirmResponseSchema = z.object({
    /** Latest retained snapshot month (`YYYY-MM`), or null before the first capture. */
    asOf: z.string().nullable(),
    firmTotals: kpiFirmTotalsSchema,
    /** The firm AUM (Discretionary) and AUA (Advisory + Execution-Only), live. */
    aumAua: kpiAumAuaSchema,
    perCategory: perCategorySchema,
    perManager: perManagerSchema,
    /** Month-over-month AUM trend, oldest first. Empty until snapshots accrue. */
    trend: z.array(kpiTrendPointSchema),
});
export type KpiFirmResponse = z.infer<typeof kpiFirmResponseSchema>;

// ---------------------------------------------------------------------------
// GET /api/kpi/my — the signed-in IM's own slice.
// ---------------------------------------------------------------------------

export const kpiMyResponseSchema = z.object({
    me: z.object({
        id: z.string(),
        displayName: z.string(),
    }),
    asOf: z.string().nullable(),
    /** `false` when the caller has no book (no holdings resolved to them). */
    hasData: z.boolean(),
    /** The caller's own current figures. */
    own: z.object({
        firmTotals: kpiFirmTotalsSchema,
        /** Discretionary (AUM) vs Advisory + XO (AUA) split of the caller's book. */
        aumAua: kpiAumAuaSchema,
        perCategory: perCategorySchema,
    }),
    /** All-IM firm figures, for "my % of firm" comparisons. */
    firm: z.object({
        firmTotals: kpiAggregateSchema,
        perCategory: perCategorySchema,
    }),
    /** The caller's own AUM trend (from retained AumSnapshot per-manager rows). */
    trend: z.array(kpiTrendPointSchema),
});
export type KpiMyResponse = z.infer<typeof kpiMyResponseSchema>;

import { errorBodyWithCode as errorBody } from '../schemas/common';

export const kpiContract = c.router(
    {
        /**
         * GET /api/kpi/firm
         *
         * Firm-wide KPI computed live from the ValuationGrid + client roster +
         * Yahoo YTD: closing AUM, client/portfolio counts and AUM-weighted YTD,
         * both firm-wide and broken down per reporting category and per
         * investment manager, plus the month-over-month AUM trend from the
         * retained `AumSnapshot` series.
         */
        getFirm: {
            method: 'GET',
            path: '/kpi/firm',
            responses: {
                200: kpiFirmResponseSchema,
            },
            summary: 'Firm-wide live KPI (AUM/counts/YTD + per-category/manager + AUM trend).',
        },

        /**
         * GET /api/kpi/my
         *
         * The Desk own-slice. Actor-resolved: the signed-in IM's own current
         * figures + the firm totals (for "my % of firm") + the IM's own AUM
         * trend. `hasData=false` when no holdings resolve to the caller.
         */
        getMy: {
            method: 'GET',
            path: '/kpi/my',
            responses: {
                200: kpiMyResponseSchema,
                404: errorBody,
            },
            summary: "The signed-in IM's own live KPI slice.",
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
